import { computed, DestroyRef, inject, Injectable, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';

import { Ability } from '@core/abilities-types';
import { CurrentUserSelectors } from '@core/account-data';
import { EmailAiGeneratorService } from '@core/email-ai-logic';
import { FeatureDiscoveryActions } from '@core/feature-discovery-data';
import { I18nService } from '@core/i18n-legacy';
import {
  flexiblePositionStrategyFactory,
  type Step,
  WalkthroughState,
  WalkthroughStepsService,
  WalkthroughTooltipComponent,
} from '@core/walkthrough-legacy';

@Injectable({ providedIn: 'root' })
export class WalkthroughAiRejectionEmailService {
  private readonly walkthroughStepsService = inject(WalkthroughStepsService);
  private readonly store = inject(Store);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly emailAiGeneratorService = inject(EmailAiGeneratorService);

  public openDropdown$ = new BehaviorSubject<boolean>(false);

  private currentUser = this.store.selectSignal(CurrentUserSelectors.data);
  private mailThreadPresent = signal(false);

  private canUseEmail = computed(() =>
    [Ability.ViewMailbox, Ability.SendEmails].every(val =>
      this.currentUser()?.roleAbilities.includes(val),
    ),
  );

  // eslint-disable-next-line max-lines-per-function
  private steps: Signal<Step[]> = computed(() => [
    {
      id: 'walkthrough-step-ai-rejection-email-1',
      position: 1,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['right', 'top'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.ai_writer_rejection.step_1.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.ai_writer_rejection.step_1.description',
          ),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.ai_writer_rejection.step_1.cta_text',
          ),
          hasNewBadge: true,
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'bottom', overlayX: 'end', overlayY: 'top' }],
        offset: { offsetX: 40, offsetY: 24 },
      },
      isHidden: !this.mailThreadPresent(),
    },
    {
      id: 'walkthrough-step-ai-rejection-email-2',
      position: 2,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'left'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.ai_writer_rejection.step_2.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.ai_writer_rejection.step_2.description',
          ),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.ai_writer_rejection.step_2.cta_text',
          ),
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' }],
        offset: { offsetX: 24, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-ai-rejection-email-3',
      position: 3,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'left'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.ai_writer_rejection.step_3.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.ai_writer_rejection.step_3.description',
          ),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.ai_writer_rejection.step_3.cta_text',
          ),
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' }],
        offset: { offsetX: 24, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-ai-rejection-email-4',
      position: 4,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['left', 'bottom'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.ai_writer_rejection.step_4.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.ai_writer_rejection.step_4.description',
          ),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.ai_writer_rejection.step_4.cta_text',
          ),
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' }],
        offset: { offsetX: 0, offsetY: -24 },
      },
    },
  ]);

  public setMailThreadPresent(value: boolean) {
    this.mailThreadPresent.set(value);
  }

  public updateOverlayPositions() {
    this.walkthroughStepsService.updateOverlayPositions();
  }

  public openAiWriterDropdown() {
    this.openDropdown$.next(true);
  }

  public closeAiWriterDropdown() {
    this.openDropdown$.next(false);
  }

  public closeAiWriterPrompt() {
    this.emailAiGeneratorService.closeMessageWindow();
  }

  public start(candidateWithEmail: boolean): void {
    if (
      !this.currentUser()?.featureDiscovery?.aiWriterRejectionEmailUsed &&
      this.emailAiGeneratorService.isEmailAiEnabled() &&
      candidateWithEmail &&
      this.canUseEmail()
    ) {
      this.closeAiWriterPrompt();
      this.walkthroughStepsService
        .start(this.getFilteredSteps())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(state => {
          if ([WalkthroughState.Finished, WalkthroughState.Canceled].includes(state)) {
            this.store.dispatch(
              FeatureDiscoveryActions.update({ payload: { aiWriterRejectionEmailUsed: true } }),
            );
            this.store.dispatch(
              FeatureDiscoveryActions.update({ payload: { aiWriterUsed: true } }),
            );
          }
        });
    }
  }

  private getFilteredSteps(): Step[] {
    return this.steps()
      .filter(step => !step.isHidden)
      .map((step, i) => ({ ...step, position: i + 1 }));
  }
}
